import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CardShell } from '../../atlas/cards/CardShell';
import { MetricRow } from '../../atlas/cards/MetricRow';
import { countYAxisReferenceGridLines } from '../../countYAxisReferenceGridLines';
import {
  aggregateQuarterlyChartToAnnual,
  attachMappedGoalTargets,
  buildCountYAxisScaleForChart,
  collectGoalValues,
  latest,
  parseQuarter,
  pctChange,
  previous,
} from '../componentProjectMetricUtils';
import { renderChartGoalLines } from '../../ChartGoalLines';
import type { SafeTComponentProjectData } from './mapSafeTComponentProjectData';

type SafeTTulsaEmploymentCardProps = {
  data: SafeTComponentProjectData;
};

export function SafeTTulsaEmploymentCard({ data }: SafeTTulsaEmploymentCardProps) {
  const theme = useMantineTheme();
  const series = data.series;
  const cur = latest(series);
  const prev = previous(series);
  const subtitle = cur?.quarter ?? 'No data';

  const qoq = cur ? pctChange(cur.tulsaEmployment, prev?.tulsaEmployment ?? null) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText =
    qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.tulsaEmployment, 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point.tulsaEmployment, 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText =
    yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const quarterlyChartData = attachMappedGoalTargets(
    series.map((point) => ({
      quarter: point.quarter,
      tulsaEmployment: point.tulsaEmployment,
    })),
    series,
    [{ dataKey: 'tulsaEmployment', goalField: 'tulsaEmployment' }]
  );
  const chartData = aggregateQuarterlyChartToAnnual(quarterlyChartData, ['tulsaEmployment']);
  const goalValues = collectGoalValues(chartData, ['tulsaEmployment']);
  const chartMax = Math.max(
    0,
    ...chartData.map((point) => Number(point.tulsaEmployment))
  );
  const { yAxisMax, yAxisTicks } = buildCountYAxisScaleForChart([chartMax], goalValues);
  const gridLineStroke =
    theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)';
  const hasData =
    series.some((point) => point.tulsaEmployment > 0) || data.allTime.tulsaEmployment > 0;

  return (
    <CardShell
      title="Companies Engaged from the Greater Tulsa Region"
      subtitle={subtitle}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip:
                'Number of companies engaged who have at least 1 full-time staff member working in Greater Tulsa Region',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.tulsaEmployment ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
              reserveGoalSublabel: true,
            },
            {
              label: 'YTD',
              labelTooltip:
                'Number of companies engaged who have at least 1 full-time staff member working in Greater Tulsa Region',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: yoyText,
              reserveGoalSublabel: true,
            },
            {
              label: 'All-Time',
              labelTooltip:
                'Number of companies engaged who have at least 1 full-time staff member working in Greater Tulsa Region',
              value: (
                <Text size="lg" weight={800}>
                  {data.allTime.tulsaEmployment}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Companies Engaged from the Greater Tulsa Region by Year
        </Text>

        <Box sx={{ height: 180 }}>
          {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} barCategoryGap="12%">
              <XAxis
                dataKey="yearLabel"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, yAxisMax]}
                ticks={yAxisTicks}
              />
              {countYAxisReferenceGridLines(yAxisTicks, gridLineStroke, goalValues)}
              {renderChartGoalLines(chartData, ['tulsaEmployment'])}
              <Tooltip
                cursor={false}
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const value = payload[0]?.value;
                  return (
                    <div style={tooltipShellStyle}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                        {label}
                      </div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>
                        {typeof value === 'number'
                          ? `${value} ${value === 1 ? 'Company' : 'Companies'}`
                          : '—'}
                      </div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="tulsaEmployment"
                fill="#845EF7"
                radius={[4, 4, 0, 0]}
                barSize={18}
                isAnimationActive={false}
                activeBar={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
          ) : (
            <Text size="sm" color="dimmed" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              No companies engaged from the Greater Tulsa Region reported yet.
            </Text>
          )}
        </Box>
      </Stack>
    </CardShell>
  );
}
